import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import Inputs from '../inputs';
import InputMaps from '../InputMaps';
import { FieldValidator } from '../../utils/fieldValidation';

interface EnhancedInlineEditorProps {
  value: any;
  fieldConfig: any; // The field configuration from user preferences (same as in createLeadForm)
  onSave: (newValue: any) => void;
  onCancel: () => void;
  position: { top: number; left: number; width: number; height: number };
}

const EditorOverlay = styled.div<{ 
  position: { top: number; left: number; width: number; height: number };
}>`
  position: fixed;
  top: ${props => props.position.top}px;
  left: ${props => props.position.left}px;
  min-width: ${props => Math.max(props.position.width, 320)}px;
  max-width: 480px;
  background: #ffffff;
  border: 2px solid #1890ff;
  border-radius: 12px;
  box-shadow: 
    0 8px 32px rgba(24, 144, 255, 0.12),
    0 4px 16px rgba(0, 0, 0, 0.08);
  z-index: 9999;
  padding: 24px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(135deg, #1890ff, #52c41a);
    border-radius: 12px;
    z-index: -1;
    opacity: 0.1;
  }
`;

const EditorHeader = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #1890ff;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: '✏️';
    font-size: 18px;
  }
`;

const EditorContent = styled.div`
  .form-group {
    margin-bottom: 20px;
  }
  
  .form-label {
    font-size: 14px;
    font-weight: 600;
    color: #262626;
    margin-bottom: 10px;
    display: block;
    letter-spacing: 0.3px;
  }
  
  .form-control {
    border: 2px solid #e8e8e8;
    border-radius: 8px;
    padding: 14px 16px;
    font-size: 16px;
    font-weight: 500;
    width: 100%;
    background: #fafafa;
    transition: all 0.2s ease;
    
    &:focus {
      border-color: #1890ff;
      background: #ffffff;
      box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.1);
      outline: none;
      transform: translateY(-1px);
    }
    
    &:hover:not(:focus) {
      border-color: #bfbfbf;
      background: #ffffff;
    }
  }
  
  .input-with-icon-wrapper {
    position: relative;
    
    .field-icon {
      position: absolute;
      left: 16px;
      top: 50%;
      transform: translateY(-50%);
      color: #8c8c8c;
      font-size: 18px;
    }
    
    .form-control {
      padding-left: 50px;
    }
  }
  
  .dropdown-menu {
    border: 2px solid #e8e8e8;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    background: #ffffff;
    max-height: 240px;
    overflow-y: auto;
    
    .dropdown-item {
      padding: 12px 16px;
      font-size: 15px;
      font-weight: 500;
      
      &:hover {
        background: #f0f7ff;
        color: #1890ff;
      }
      
      .form-check-input {
        margin-right: 12px;
        transform: scale(1.2);
      }
    }
  }
`;

const ErrorMessage = styled.div`
  color: #ff4d4f;
  font-size: 13px;
  font-weight: 500;
  margin-top: 8px;
  margin-bottom: 12px;
  padding: 8px 12px;
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &::before {
    content: '⚠️';
    font-size: 14px;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 2px solid #f0f0f0;
`;

const SaveButton = styled(Button)`
  &.ant-btn-primary {
    background: linear-gradient(135deg, #52c41a 0%, #73d13d 100%);
    border: none;
    height: 44px;
    padding: 0 24px;
    font-size: 15px;
    font-weight: 600;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(82, 196, 26, 0.3);
    
    &:hover, &:focus {
      background: linear-gradient(135deg, #73d13d 0%, #95de64 100%);
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(82, 196, 26, 0.4);
    }
    
    .anticon {
      font-size: 16px;
    }
  }
`;

const CancelButton = styled(Button)`
  &.ant-btn {
    color: #8c8c8c;
    background: #f5f5f5;
    border: 2px solid #d9d9d9;
    height: 44px;
    padding: 0 24px;
    font-size: 15px;
    font-weight: 600;
    border-radius: 8px;
    
    &:hover, &:focus {
      color: #ff4d4f;
      background: #fff2f0;
      border-color: #ffccc7;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(255, 77, 79, 0.2);
    }
    
    .anticon {
      font-size: 16px;
    }
  }
`;

const EnhancedInlineEditor: React.FC<EnhancedInlineEditorProps> = ({
  value,
  fieldConfig,
  onSave,
  onCancel,
  position
}) => {
  const [editValue, setEditValue] = useState(() => {
    // Handle different field types for initial value
    if (fieldConfig.type === 'multi-selection') {
      return Array.isArray(value) ? value : [];
    }
    return value || '';
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [locationData, setLocationData] = useState(value || {});
  const [validationError, setValidationError] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const selectRef = useRef<any>(null);

  // Auto-focus and cursor positioning
  useEffect(() => {
    const timer = setTimeout(() => {
      if (fieldConfig.type === 'selection') {
        // For regular select dropdowns, focus and open
        if (selectRef.current) {
          selectRef.current.focus();
          selectRef.current.click();
        }
      } else if (fieldConfig.type === 'multi-selection') {
        // For multi-select dropdowns, try to open Ant Design dropdown
        const selectElement = document.querySelector('.ant-select-selector');
        if (selectElement) {
          (selectElement as HTMLElement).click();
        }
      } else if (inputRef.current) {
        // For text/number inputs, focus and position cursor at end
        inputRef.current.focus();
        // Try to set selection range - some input types don't support it
        try {
          const len = inputRef.current.value.length;
          inputRef.current.setSelectionRange(len, len);
        } catch (error) {
          // Some input types don't support setSelectionRange, ignore the error
        }
      }
    }, 150);
    
    return () => clearTimeout(timer);
  }, [fieldConfig.type]);

  const handleSave = async () => {
    // Clear any previous validation errors
    setValidationError('');
    
    // Validate field value before saving
    if (fieldConfig.type && typeof editValue === 'string') {
      const validation = FieldValidator.validateField(editValue, fieldConfig.type);
      if (!validation.isValid) {
        setValidationError(validation.message || 'Invalid value');
        return; // Don't save if validation fails
      }
    }
    
    setIsLoading(true);
    try {
      // Format the value before saving
      let finalValue = fieldConfig.type === 'location' ? locationData : editValue;
      
      // Format the value if it's a string and has a specific type
      if (typeof finalValue === 'string' && fieldConfig.type) {
        finalValue = FieldValidator.formatValue(finalValue, fieldConfig.type);
      }
      
      await onSave(finalValue);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  const handleChange = (name: string, newValue: string | string[]) => {
    setEditValue(newValue);
    
    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError('');
    }
  };

  const handleMapChange = (name: string, address: string, coords: any) => {
    const locationObject = {
      address: address,
      coords: {
        latitude: coords.lat,
        longitude: coords.lng,
        latitudeDelta: 0.0922,
        longitudeDelta: 0.0922
      }
    };
    setLocationData(locationObject);
  };

  const renderField = () => {
    // Use the same logic as createLeadForm for consistent rendering
    if (fieldConfig.type === "location") {
      return (
        <InputMaps
          disabled={false}
          type={fieldConfig.type}
          name={fieldConfig.value}
          value={fieldConfig.type === "location" ? (locationData?.address || "") : editValue}
          locationData={locationData}
          onChange={handleMapChange}
          className="form-control"
          label={fieldConfig.label}
          isRequired={fieldConfig.isRequired}
        />
      );
    } else if (fieldConfig.type === "date") {
      return (
        <div className="form-group">
          <label className="form-label">
            {fieldConfig.label}
            {fieldConfig.isRequired && <span style={{ color: "red" }}>*</span>}
          </label>
          <input
            ref={inputRef}
            type="date"
            className="form-control"
            value={editValue}
            onChange={(e) => handleChange(fieldConfig.value, e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Enter Date"
          />
        </div>
      );
    } else if (fieldConfig.type === "selection") {
      // Custom selection dropdown with auto-open
      return (
        <div className="form-group">
          <label className="form-label">
            {fieldConfig.label}
            {fieldConfig.isRequired && <span style={{ color: "red" }}>*</span>}
          </label>
          <div className="input-with-icon-wrapper">
            <div className="field-icon">📋</div>
            <select
              ref={selectRef}
              className="form-control"
              value={editValue}
              onChange={(e) => handleChange(fieldConfig.value, e.target.value)}
              onKeyDown={handleKeyPress}
              autoFocus
            >
              <option value="">Select {fieldConfig.label}</option>
              {fieldConfig.options?.map((option: any, i: number) => (
                <option value={option} key={i}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>
      );
    } else if (fieldConfig.type === "multi-selection") {
      // Use Inputs component for multi-selection (complex dropdown)
      return (
        <Inputs
          disabled={false}
          name={fieldConfig.value}
          type={fieldConfig.type}
          autoComplete="off"
          className="form-control"
          placeholder={`Select ${fieldConfig.label.toLowerCase()}`}
          value={editValue}
          onChange={(e: any) => handleChange(fieldConfig.value, e.target.value)}
          label={fieldConfig.label}
          isRequired={fieldConfig.isRequired}
          options={fieldConfig.options}
          id="inline_editor_multiselect"
        />
      );
    } else {
      // Direct input for text, number, amount, phone etc.
      const inputType = fieldConfig.type === "phone" || fieldConfig.type === "amount" ? "number" : 
                       fieldConfig.type === "email" ? "email" :
                       fieldConfig.type === "url" ? "url" : "text";
      
      const getIcon = () => {
        switch (fieldConfig.type) {
          case "name": return "👤";
          case "email": return "📧";
          case "phone": return "📞";
          case "amount": return "💰";
          case "url": return "🔗";
          default: return "✏️";
        }
      };

      return (
        <div className="form-group">
          <label className="form-label">
            {fieldConfig.label}
            {fieldConfig.isRequired && <span style={{ color: "red" }}>*</span>}
          </label>
          <div className="input-with-icon-wrapper">
            <div className="field-icon">{getIcon()}</div>
            {fieldConfig.label === "Notes" ? (
              <textarea
                ref={inputRef as any}
                className="form-control"
                value={editValue}
                onChange={(e) => handleChange(fieldConfig.value, e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={`Enter ${fieldConfig.label.toLowerCase()}`}
                rows={4}
                style={{ resize: 'vertical', minHeight: '80px' }}
              />
            ) : (
              <input
                ref={inputRef}
                type={inputType}
                className="form-control"
                value={editValue}
                onChange={(e) => handleChange(fieldConfig.value, e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={`Enter ${fieldConfig.label.toLowerCase()}`}
              />
            )}
          </div>
        </div>
      );
    }
  };

  return (
    <EditorOverlay position={position}>
      <EditorHeader>
        Edit {fieldConfig.label}
      </EditorHeader>
      <EditorContent>
        {renderField()}
        {validationError && (
          <ErrorMessage>
            {validationError}
          </ErrorMessage>
        )}
        <ButtonRow>
          <CancelButton 
            size="small" 
            onClick={onCancel}
            icon={<CloseOutlined />}
          >
            Cancel
          </CancelButton>
          <SaveButton 
            type="primary" 
            size="small" 
            onClick={handleSave}
            loading={isLoading}
            icon={<CheckOutlined />}
          >
            Save
          </SaveButton>
        </ButtonRow>
      </EditorContent>
    </EditorOverlay>
  );
};

export default EnhancedInlineEditor;